import tenantTheme from '@theme';
import React, { forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Drawer, Flex, Icon } from '../../common';
import { formatWholeNumber } from '../../../utility/utility';
import { getTimeDateString } from '../../../utility/date';
import {
  QUOTA_OTHERS_KEY,
  getPackageDaysLeft,
  getQuotaProductDescription,
  getQuotaSegmentColors,
  getQuotaSideDrawerProps,
  isYearlyPackage,
} from '../../../utility/quota';
import ExpandableBottomSheet from '../../assign-products/expandable-bottom-sheet';
import { DrawerProductRow, DrawerSection, PackageBanner, StackBar, Swatch } from './styled';

// Wait for the drawer to finish sliding in before scrolling inside it.
const FOCUS_SCROLL_DELAY = 450;

const ProductRow = ({ product, isAgencyView, focused }) => {
  const { t } = useTranslation();
  const colors = getQuotaSegmentColors(product.iconColor);
  const segments = isAgencyView
    ? [
        {
          key: 'not-assigned',
          value: product.notAssigned,
          color: colors.main,
          label: t('{{count}} not assigned', { count: formatWholeNumber(product.notAssigned) }),
        },
        {
          key: 'with-agents',
          value: product.withAgents,
          color: colors.soft,
          label: t('{{count}} with agents', { count: formatWholeNumber(product.withAgents) }),
        },
        {
          key: 'used',
          value: product.used,
          color: colors.used,
          label: t('{{count}} used', { count: formatWholeNumber(product.used) }),
        },
      ]
    : [
        {
          key: 'available',
          value: product.available,
          color: colors.main,
          label: t('{{count}} available', { count: formatWholeNumber(product.available) }),
        },
        {
          key: 'used',
          value: product.used,
          color: colors.used,
          label: t('{{count}} used', { count: formatWholeNumber(product.used) }),
        },
      ];
  return (
    <DrawerProductRow data-quota-row={product.slug} className={focused ? 'is-focus' : undefined}>
      <div className="row-name">
        <span className="row-icon">
          <Icon icon={product.icon} size="20px" color={product.iconColor} />
        </span>
        <div>
          <b>{t(product.name)}</b>
          <small>{t(getQuotaProductDescription(product.slug))}</small>
        </div>
      </div>
      <div className="row-amount">
        <b>{formatWholeNumber(product.available)}</b>
        <span>{t('of {{total}}', { total: formatWholeNumber(product.total) })}</span>
      </div>
      <div>
        <StackBar $height={8}>
          {segments.map((segment) => (
            <i key={segment.key} style={{ flex: `${segment.value || 0} 1 0`, background: segment.color }} />
          ))}
          {!product.total && <i style={{ flex: '1 1 0' }} />}
        </StackBar>
        <div className="row-legend">
          {segments.map((segment) => (
            <span key={segment.key}>
              <Swatch $color={segment.color} />
              {segment.label}
            </span>
          ))}
        </div>
      </div>
    </DrawerProductRow>
  );
};

// "View all" on the products card: every product with its balance, split as the agency (or the user) holds it.
const QuotaProductsDrawer = forwardRef(
  ({ data, isAgencyView, isAgent, canManage, canBuy, onAssign, onBuy, focus }, ref) => {
    const { t } = useTranslation();
    const { locale, isMobile } = useSelector((state) => state.app.AppConfig);
    const contentRef = useRef();
    const pkg = data?.package;
    const left = getPackageDaysLeft(pkg?.end_date);
    const notAssigned = data?.totals?.notAssigned;
    const focusSlug = focus?.slug;

    // Opened from a tile: bring that product (or the add-ons) into view once the drawer has slid in. Only the
    // drawer's own body scrolls: scrollIntoView also moved the page behind it, and running it mid-slide made the
    // opening judder.
    useEffect(() => {
      if (!focusSlug) return undefined;
      const timer = setTimeout(() => {
        const row = contentRef.current?.querySelector(`[data-quota-row="${focusSlug}"]`);
        let body = row?.parentElement;
        while (body && body !== document.body && !/(auto|scroll)/.test(getComputedStyle(body).overflowY)) {
          body = body.parentElement;
        }
        if (!row || !body || body === document.body) return;
        const top = row.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 8;
        body.scrollTo?.({ top, behavior: 'smooth' });
      }, FOCUS_SCROLL_DELAY);
      return () => clearTimeout(timer);
    }, [focus]);

    const footer =
      (isAgencyView && canManage) || canBuy ? (
        <Flex
          align={isMobile ? 'stretch' : 'center'}
          justify="space-between"
          gap={isMobile ? '10px' : '12px'}
          vertical={isMobile}
        >
          {isAgencyView && notAssigned != null ? (
            <Flex align="center" gap="6px" style={{ color: tenantTheme['text-color-secondary'] }}>
              <Icon icon="FiUsers" size="16px" />
              <span>
                <b style={{ color: tenantTheme['text-color'] }}>{formatWholeNumber(notAssigned)}</b>{' '}
                {t('not assigned to agents yet')}
              </span>
            </Flex>
          ) : (
            <span />
          )}
          <Flex gap="10px" style={isMobile ? { width: '100%' } : undefined}>
            {isAgencyView && canManage && (
              <Button type="default" icon="FiUsers" onClick={onAssign} className={isMobile ? 'w-100' : undefined}>
                {t('Assign to Staff')}
              </Button>
            )}
            {canBuy && (
              <Button
                type="primary"
                icon="IoAddCircleOutline"
                onClick={onBuy}
                className={isMobile ? 'w-100' : undefined}
              >
                {t('Buy More')}
              </Button>
            )}
          </Flex>
        </Flex>
      ) : null;

    const Container = isMobile ? ExpandableBottomSheet : Drawer;
    return (
      <Container
        ref={ref}
        title={<div className="fw-700">{t('Available Products')}</div>}
        {...(!isMobile && {
          ...getQuotaSideDrawerProps(locale),
          bodyStyle: { paddingTop: 16 },
          // rows are already mounted, so the first open doesn't build them mid-slide
          forceRender: true,
        })}
        footer={footer}
      >
        <div ref={contentRef}>
          {!!pkg?.name && (
            <PackageBanner>
              <Icon icon="BsBoxSeam" size="22px" color={tenantTheme['primary-color']} />
              <div className="banner-text">
                <b>{t(pkg.name)}</b>
                <small>
                  {isYearlyPackage(pkg) ? `${t('Yearly')} · ` : ''}
                  {!!pkg.end_date && t('ends {{date}}', { date: getTimeDateString(pkg.end_date, undefined, true) })}
                </small>
              </div>
              {left != null && <span className="banner-days">{t('{{count}} days left', { count: left })}</span>}
            </PackageBanner>
          )}
          {!isAgencyView && isAgent && (
            <div className="mt-16" style={{ color: tenantTheme['text-color-secondary'] }}>
              <b style={{ color: tenantTheme['text-color'] }}>{t('Assigned to you')}</b> {t('by your agency')}
            </div>
          )}
          <DrawerSection>
            <div className="section-head">
              <b>{t('Listings and upgrades')}</b>
              <span>{t('{{count}} products', { count: data?.core?.length ?? 0 })}</span>
            </div>
            {(data?.core || []).map((product) => (
              <ProductRow
                key={product.slug}
                product={product}
                isAgencyView={isAgencyView}
                focused={product.slug === focusSlug}
              />
            ))}
          </DrawerSection>
          {!!data?.others?.length && (
            <DrawerSection>
              <div className="section-head" data-quota-row={QUOTA_OTHERS_KEY}>
                <b>{t('Add-ons')}</b>
                <span>{t('{{count}} add-ons', { count: data.others.length })}</span>
              </div>
              {data.others.map((product) => (
                <ProductRow
                  key={product.slug}
                  product={product}
                  isAgencyView={isAgencyView}
                  focused={focusSlug === QUOTA_OTHERS_KEY}
                />
              ))}
            </DrawerSection>
          )}
        </div>
      </Container>
    );
  },
);

QuotaProductsDrawer.displayName = 'QuotaProductsDrawer';

export default QuotaProductsDrawer;
